import i18n from '@/i18n'

/**
 * Umanizza i messaggi di errore mostrati all'utente.
 *
 * Principio di prodotto (empatia/supporto): un errore non deve mai suonare come un
 * rimprovero o un muro tecnico. Quando il backend fornisce un messaggio specifico e
 * leggibile, lo teniamo (è spesso la spiegazione più utile). Quando invece è generico,
 * tecnico o mancante, lo sostituiamo con un messaggio caldo che dice cosa è successo
 * e, dove possibile, cosa fare — senza colpevolizzare.
 */

// Messaggi del backend troppo tecnici/generici da mostrare così come sono.
const TECHNICAL_PATTERNS = [
  /^error$/i,
  /^internal server error$/i,
  /^request failed/i,
  /^network error$/i,
  /^bad request$/i,
  /^unauthorized$/i,
  /^forbidden$/i,
  /^not found$/i,
  /^unprocessable entity$/i,
  /^validation error$/i,
  /^errore di rete$/i,
  /^errore di validazione$/i,
  /statuscode/i,
  /\bECONN|\bETIMEDOUT|\bENOTFOUND/,
]

function looksTechnical(msg: string): boolean {
  const m = msg.trim()
  if (!m) return true
  if (m.length > 160) return true // messaggi lunghissimi = stack/tecnici
  return TECHNICAL_PATTERNS.some((re) => re.test(m))
}

/** Messaggio empatico per fascia di status HTTP, localizzato (fallback italiano). */
function byStatus(status: number | undefined): string {
  const t = (k: string, d: string) => {
    const v = i18n.t(`errors.${k}` as any)
    return typeof v === 'string' && v !== `errors.${k}` ? v : d
  }
  if (status === undefined || status === 0) return t('network', 'Sembra esserci un problema di connessione. Controlla la rete e riprova tra poco.')
  if (status === 403) return t('forbidden', 'Questa sezione fa parte di un percorso dedicato: continua a crescere su eDive per accedervi.')
  if (status === 404) return t('notFound', 'Non abbiamo trovato quello che cercavi. Potrebbe essere stato spostato.')
  if (status === 408 || status === 504) return t('timeout', 'La richiesta ha impiegato troppo tempo. Riprova tra un momento.')
  if (status === 409) return t('conflict', 'Qualcosa è cambiato nel frattempo. Ricarica e riprova.')
  if (status === 413) return t('tooLarge', 'Il file è troppo grande. Prova a comprimerlo o a ridurne le dimensioni.')
  if (status === 422 || status === 400) return t('validation', 'Alcuni dati non sono nel formato giusto. Controlla i campi e riprova.')
  if (status === 429) return t('rateLimit', 'Un attimo di respiro: troppe richieste ravvicinate. Riprova tra qualche secondo.')
  if (status >= 500) return t('server', 'Un piccolo intoppo dalla nostra parte. Stiamo sistemando: riprova tra poco.')
  return t('generic', 'Qualcosa non è andato come previsto. Riprova, e se il problema resta scrivici.')
}

/**
 * Ritorna il messaggio da mostrare all'utente.
 * @param backendMsg messaggio grezzo dal backend (può essere tecnico o mancante)
 * @param status     status HTTP della risposta
 */
export function humanizeError(backendMsg: string | undefined, status: number | undefined): string {
  if (backendMsg && !looksTechnical(backendMsg)) return backendMsg
  return byStatus(status)
}
